import { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography
} from '@mui/material';
import { ReactTable } from '@components';
import {
    MRT_ShowHideColumnsButton,
    MRT_ToggleDensePaddingButton,
    MRT_ToggleFullScreenButton,
    MRT_ToggleGlobalFilterButton
} from 'material-react-table';
import EexTradeModal from './EexTradeModal';
import EexInsertOrderModal from './EexInsertOrderModal';
import TestBidAskModal from './TestBidAskModal';

const getNumericValue = (value, fallback = 0) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
};

const formatNumber = (value, digits = 3) =>
    value != null && Number.isFinite(value) ? value.toFixed(digits) : '—';

export default function Eex({
    rows,
    products,
    connectionState,
    error,
    onOrderUpdated
}) {
    const [bidModalOpen, setBidModalOpen] = useState(false);
    const [bidModalInitialTab, setBidModalInitialTab] = useState(0);
    const [bidModalEntry, setBidModalEntry] = useState(null);
    const [askModalOpen, setAskModalOpen] = useState(false);
    const [askModalInitialTab, setAskModalInitialTab] = useState(0);
    const [askModalEntry, setAskModalEntry] = useState(null);

    // Test bid/ask modal state
    const [testBidModalOpen, setTestBidModalOpen] = useState(false);
    const [testAskModalOpen, setTestAskModalOpen] = useState(false);

    // Insert new order modal state
    const [insertOrderModalOpen, setInsertOrderModalOpen] = useState(false);
    const [insertOrderModalEntry, setInsertOrderModalEntry] = useState(null);

    const getDefaultTradeEntry = () => {
        const firstRow = rows[0];
        const firstProduct = products[0];

        if (!firstRow || !firstProduct || !firstRow[firstProduct]) {
            return null;
        }

        return {
            row: firstRow,
            productName: firstProduct
        };
    };

    const handleOpenBidModal = (entry, tabIndex = 0) => {
        if (!entry) return;

        setBidModalEntry({ ...entry, side: 'bid' });
        setBidModalInitialTab(tabIndex);
        setBidModalOpen(true);
    };

    const handleCloseBidModal = () => {
        setBidModalOpen(false);
    };

    const handleOpenAskModal = (entry, tabIndex = 0) => {
        if (!entry) return;

        setAskModalEntry({ ...entry, side: 'ask' });
        setAskModalInitialTab(tabIndex);
        setAskModalOpen(true);
    };

    const handleCloseAskModal = () => {
        setAskModalOpen(false);
    };

    const handleOpenTestBidModal = () => {
        setTestBidModalOpen(true);
    };

    const handleCloseTestBidModal = () => {
        setTestBidModalOpen(false);
    };

    const handleOpenTestAskModal = () => {
        setTestAskModalOpen(true);
    };

    const handleCloseTestAskModal = () => {
        setTestAskModalOpen(false);
    };

    // const handleOpenInsertOrderModal = (entry) => {
    //     const resolvedEntry = entry || getDefaultTradeEntry();
    //     if (!resolvedEntry) return;
    //     setInsertOrderModalEntry(resolvedEntry);
    //     setInsertOrderModalOpen(true);
    // };

    const handleOpenInsertOrderModal = (entry) => {
        const resolvedEntry = entry ||
            getDefaultTradeEntry() || {
                row: { Contract: 'DA' },
                productName: 'TTF'
            };

            console.log('handleOpenInsertOrderModal: resolvedEntry', resolvedEntry);

        setInsertOrderModalEntry(resolvedEntry);
        setInsertOrderModalOpen(true);
    };

    const handleCloseInsertOrderModal = () => {
        setInsertOrderModalOpen(false);
    };

    const columns = useMemo(() => {
        const product = (p) => ({
            header: p,
            id: p,
            muiTableHeadCellProps: {
                sx: {
                    textAlign: 'center',
                    borderRight:
                        p === 'THE' || p === 'TTF' || p === 'PEG'
                            ? '2px solid #3DC3C1'
                            : 'none',
                    pr: 0.5
                }
            },
            columns: [
                {
                    header: 'PID',
                    id: `${p}_PID`,
                    accessorFn: (row) => row[p]?.OrderId || p,
                    size: 60,
                    Cell: ({ cell }) => (
                        <Box sx={{ textAlign: 'center', fontSize: '14px' }}>
                            {cell.getValue()}
                        </Box>
                    )
                },
                {
                    header: 'Qty',
                    id: `${p}_BidQty`,
                    accessorFn: (row) => row[p]?.BidQty || 0,
                    size: 60,
                    Cell: ({ cell }) => (
                        <Box sx={{ textAlign: 'center', fontSize: '14px' }}>
                            {cell.getValue()}
                        </Box>
                    )
                },
                {
                    header: 'Bid',
                    id: `${p}_Bid`,
                    accessorFn: (row) => formatNumber(row[p]?.Bid),
                    size: 60,

                    Cell: ({ cell, row }) => (
                        <Box sx={{ textAlign: 'center', fontSize: '14px' }}>
                            <Button
                                size="small"
                                variant="text"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenBidModal(
                                        {
                                            row: row.original,
                                            productName: p
                                        },
                                        0
                                    );
                                }}
                                sx={{
                                    minWidth: 0,
                                    px: 0.5,
                                    py: 0,
                                    my: 0,
                                    textTransform: 'none',
                                    backgroundColor: '#e8f5ff'
                                }}
                            >
                                {cell.getValue()}
                            </Button>
                        </Box>
                    )
                },
                {
                    header: 'Ask',
                    id: `${p}_Ask`,
                    accessorFn: (row) => formatNumber(row[p]?.Ask),
                    size: 60,
                    Cell: ({ cell, row }) => (
                        <Box sx={{ textAlign: 'center', fontSize: '14px' }}>
                            <Button
                                size="small"
                                variant="text"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenAskModal(
                                        {
                                            row: row.original,
                                            productName: p
                                        },
                                        0
                                    );
                                }}
                                sx={{
                                    minWidth: 0,
                                    px: 0.5,
                                    py: 0,
                                    my: 0,
                                    textTransform: 'none',
                                    backgroundColor: '#fff3e0'
                                }}
                            >
                                {cell.getValue()}
                            </Button>
                        </Box>
                    )
                },
                {
                    header: 'Qty',
                    id: `${p}_AskQty`,
                    accessorFn: (row) => row[p]?.AskQty || 0,
                    size: 60,
                    muiTableBodyCellProps: {
                        sx: {
                            textAlign: 'center',
                            borderRight:
                                p === 'THE' || p === 'TTF' || p === 'PEG'
                                    ? '2px solid red'
                                    : 'none'
                        }
                    },
                    Cell: ({ cell }) => (
                        <Box sx={{ textAlign: 'center' }}>
                            {cell.getValue()}
                        </Box>
                    )
                }
            ]
        });

        return [
            {
                header: 'Contract',
                id: 'Contract',
                accessorFn: (row) => row.Contract,
                size: 60,
                muiTableHeadCellProps: {
                    sx: {
                        textAlign: 'left',
                        borderRight: '2px solid #3DC3C1',
                        pl: 1
                    }
                },
                muiTableBodyCellProps: {
                    sx: {
                        textAlign: 'left',
                        borderRight: '2px solid #3DC3C1',
                        pl: 1
                    }
                }
            },
            ...products.map((p) => product(p))
        ];
    }, [products]);

    return (
        <Box sx={{ width: '100%', p: 0 }}>
            <EexTradeModal
                open={bidModalOpen}
                initialTab={bidModalInitialTab}
                tradeEntry={bidModalEntry}
                onClose={handleCloseBidModal}
                side="bid"
                rows={rows}
                products={products}
                onOrderUpdated={onOrderUpdated}
            />
            <EexTradeModal
                open={askModalOpen}
                initialTab={askModalInitialTab}
                tradeEntry={askModalEntry}
                onClose={handleCloseAskModal}
                side="ask"
                rows={rows}
                products={products}
                onOrderUpdated={onOrderUpdated}
            />
            <TestBidAskModal
                open={testBidModalOpen}
                side="bid"
                onClose={handleCloseTestBidModal}
            />
            <TestBidAskModal
                open={testAskModalOpen}
                side="ask"
                onClose={handleCloseTestAskModal}
            />
            <EexInsertOrderModal
                open={insertOrderModalOpen}
                tradeEntry={insertOrderModalEntry}
                onClose={handleCloseInsertOrderModal}
            />
            <Stack spacing={0.5}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap'
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        {(connectionState === 'connecting' ||
                            connectionState === 'reconnecting') && (
                            <CircularProgress size={18} />
                        )}
                        <Typography variant="body1">
                            Status:{' '}
                            <strong>
                                {connectionState === 'connected'
                                    ? 'Connected'
                                    : connectionState === 'reconnecting'
                                      ? 'Reconnecting'
                                      : connectionState === 'closed'
                                        ? 'Disconnected'
                                        : 'Connecting'}
                            </strong>
                        </Typography>
                    </Stack>

                    {/* <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={handleOpenTestBidModal}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#29a000',
                                color: '#29a000',
                                fontSize: '12px',
                                '&:hover': {
                                    borderColor: '#29a000',
                                    bgcolor: '#e8f5e9'
                                }
                            }}
                        >
                            🧪 Test Bid
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={handleOpenTestAskModal}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#ff6d00',
                                color: '#ff6d00',
                                fontSize: '12px',
                                '&:hover': {
                                    borderColor: '#ff6d00',
                                    bgcolor: '#fff3e0'
                                }
                            }}
                        >
                            🧪 Test Ask
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                            Last update:{' '}
                            {lastMessageAt
                                ? lastMessageAt.toLocaleString()
                                : 'No data yet'}
                        </Typography>
                    </Stack> */}
                </Box>

                {error && <Alert severity="error">{error}</Alert>}

                {connectionState !== 'connected' && rows.length === 0 && (
                    <Alert severity="info">
                        Waiting for EEX stream data. The page will
                        auto-reconnect if the socket closes.
                    </Alert>
                )}
                <Box sx={{ mt: 0.5 }}>
                    <ReactTable
                        headerText="Live EEX Orders"
                        columns={columns}
                        data={rows}
                        isLoading={connectionState === 'connecting'}
                        showCellBorders={true}
                        renderToolbarInternalActions={({ table }) => (
                            <Box
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}
                            >
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => handleOpenInsertOrderModal()}
                                    sx={{
                                        textTransform: 'none',
                                        bgcolor: '#245372',
                                        color: '#fff',
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        '&:hover': { bgcolor: '#1a3d54' }
                                    }}
                                >
                                    + New Order
                                </Button>
                                <MRT_ToggleGlobalFilterButton table={table} />
                                <MRT_ShowHideColumnsButton table={table} />
                                <MRT_ToggleDensePaddingButton table={table} />
                                <MRT_ToggleFullScreenButton table={table} />
                            </Box>
                        )}
                        muiTableContainerProps={{
                            sx: {
                                maxHeight: 'calc(100vh - 220px)',
                                overflow: 'auto'
                            }
                        }}
                        muiTableHeadCellProps={{
                            sx: {
                                py: 0,
                                px: 0.75,
                                fontSize: '12px'
                            }
                        }}
                        muiTableBodyCellProps={{
                            sx: {
                                py: 0,
                                px: 0.75,
                                fontSize: '12px'
                            }
                        }}
                        // density="spacious"
                    />
                </Box>

                {rows.length === 0 && connectionState === 'connected' && (
                    <Alert severity="info">No orders received yet.</Alert>
                )}
            </Stack>
        </Box>
    );
}
